import { useEffect, useState } from "react";

import RequestDemo from "./request-demo";
import Calendar from "./calendar";
import { ArrowLeft, Calendar1 } from "lucide-react";
import { systemTimeZone, viewerTimeZone } from "./timezoneutils";
import MeetingConfirmed from "./meetingconfirmed";

export default function EventifyScheduler() {
  const userZone = viewerTimeZone();

  const [selectedTimeZone, setSelectedTimeZone] = useState(systemTimeZone());

  const [resourceData, setResourceData] = useState();

  const [currentStep, setCurrentStep] = useState("calendar");

  const [isFetching, setIsFetching] = useState(false);

  const [isCreatingMeeting, setIsCreatingMeeting] = useState(false);
  const [isMeetingCreated, setIsMeetingCreated] = useState(false);
  const [isMeetingRescheduled, setIsMeetingRescheduled] = useState(false);
  const [isRescheduleInitiated, setIsRescheduleInitiated] = useState(false);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [isInviting, setIsInviting] = useState(false);

  const [hasError, setHasError] = useState(false);

  const [selectedTime, setSelectedTime] = useState();

  const [demoData, setDemoData] = useState();

  const [finalSubmittedData, setFinalSubmittedData] = useState();

  const [eventData, setEventData] = useState();

  const BASEURL = "https://staging.teks.co.in/requestdemo/calendar.php";
  const STAGING_BASE_URL =
    "https://ecrm.staging.teks.co.in/public/index.php/api/";

  const fetchAvailableDates = () => {
    setIsFetching(true);
    fetch(
      `${STAGING_BASE_URL}availabletime?owner_timezone=${userZone}&viewer_timezone=${selectedTimeZone.zone}`
    )
      .then((response) => {
        if (!response.ok) {
          // This will catch 4xx or 5xx errors
          throw new Error(
            `HTTP Error ${response.status}: ${response.statusText}`
          );
        }
        return response.json(); // Only try parsing if response is OK
      })
      .then((response) => {
        if (response.success === 1) {
          setResourceData(response.data);
          setIsFetching(false);
        }
      })
      .catch((error) => {
        setHasError(true);
        setIsFetching(false);
        setIsCreatingMeeting(false);
        setIsMeetingCreated(false);
      });
  };

  useEffect(() => {
    if (selectedTimeZone) {
      fetchAvailableDates();
    }
  }, [selectedTimeZone]);

  const createDemoRequest = (data) => {
    const selectedSlot = resourceData?.available_slots?.find(
      (slot) => slot.start === selectedTime.start
    );
    const randomIndex = Math.floor(
      Math.random() * selectedSlot?.available_users.length
    );
    const ownerId = selectedSlot?.available_users[randomIndex];
    const ownerDetails = resourceData?.all_users?.find(
      (data) => data.userid === ownerId
    );

    const formData = {
      ...data,
      ...selectedTime,
      lead_ownerId: selectedSlot?.available_users[randomIndex],
      all_available_users: selectedSlot?.available_users,
    };

    setFinalSubmittedData({
      selectedTime,
      selectedTimeZone,
      demoData: data,
      lead_owner: ownerDetails,
    });

    setIsCreatingMeeting(true);

    fetch(`${STAGING_BASE_URL}sendmeeting`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    })
      .then((response) => {
        if (!response.ok) {
          // This will catch 4xx or 5xx errors
          throw new Error(
            `HTTP Error ${response.status}: ${response.statusText}`
          );
        }
        return response.json(); // Only try parsing if response is OK
      })
      .then((response) => {
        if (response.success === 1) {
          setIsCreatingMeeting(false);
          setIsMeetingCreated(true);
          setEventData(response.data);
        } else {
          setHasError(true);
        }
      })
      .catch((error) => {
        setHasError(true);
      });
  };

  const initiateRescheduleRequest = (eventId, newTime) => {
    setIsRescheduling(true);

    setFinalSubmittedData({
      ...finalSubmittedData,
      selectedTime: newTime,
      selectedTimeZone,
      demoData,
    });

    fetch(`${STAGING_BASE_URL}reschedule`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        meetingid: eventId,
        lead_ownerId: finalSubmittedData.lead_owner.userid,
        ...newTime,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          // This will catch 4xx or 5xx errors
          throw new Error(
            `HTTP Error ${response.status}: ${response.statusText}`
          );
        }
        return response.json(); // Only try parsing if response is OK
      })
      .then((response) => {
        if (response.success === 1) {
          setIsRescheduling(false);
          setIsRescheduleInitiated(false);
          setIsMeetingRescheduled(true);
        } else {
          setHasError(true);
        }
      })
      .catch((error) => {
        setHasError(true);
      });
  };

  const addMoreAttendees = (eventId, emails) => {
    setIsInviting(true);

    fetch(`${STAGING_BASE_URL}invites`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        meetingid: eventId,
        emails,
        lead_ownerId: finalSubmittedData.lead_owner.userid,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          // This will catch 4xx or 5xx errors
          throw new Error(
            `HTTP Error ${response.status}: ${response.statusText}`
          );
        }
        return response.json(); // Only try parsing if response is OK
      })
      .then((response) => {
        if (response.success === 1) {
          setEventData(response.data);
          setTimeout(() => {
            setIsInviting(false);
          }, 100);
        } else {
          setHasError(true);
        }
      })
      .catch((error) => {
        setHasError(true);
      });
  };

  const cancelEvent = (eventId) => {
    setIsCancelling(true);
    setIsMeetingRescheduled(false);

    fetch(`${STAGING_BASE_URL}cancelevent`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        meetingid: eventId,
        lead_ownerId: finalSubmittedData.lead_owner.userid,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          // This will catch 4xx or 5xx errors
          throw new Error(
            `HTTP Error ${response.status}: ${response.statusText}`
          );
        }
        return response.json(); // Only try parsing if response is OK
      })
      .then((response) => {
        if (response.success === 1) {
          setIsCancelling(false);
          setIsCancelled(true);
        } else {
          setHasError(true);
        }
      })
      .catch((error) => {
        setHasError(true);
      });
  };

  const handleShowDemoForm = () => {
    setDemoData(null);
    setCurrentStep("demo");
  };

  const handleCloseDemoForm = () => {
    setCurrentStep("calendar");
  };

  const handleStepBackNavigation = () => {
    if (isRescheduleInitiated) {
      setIsRescheduleInitiated(false);
    } else {
      if (currentStep === "demo") {
        setCurrentStep("calendar");
      }
    }
  };

  return (
    <div className="relative w-full h-screen flex flex-col overflow-x-hidden bg-gray-100">
      {/* Header - Full Width */}
      <div className="sticky top-0 z-50 bg-white">
        <div className="flex items-center justify-between px-2 sm:px-6 h-10">
          <div className="flex items-center justify-center w-10 h-10 -ml-1">
            {!(
              currentStep === "calendar" ||
              isCreatingMeeting ||
              isMeetingCreated ||
              isMeetingRescheduled ||
              isRescheduleInitiated
            ) && (
              <button
                onClick={() => handleStepBackNavigation()}
                className="flex items-center justify-center w-full h-full hover:bg-gray-100 rounded-full transition-colors"
              >
                <ArrowLeft size={20} className="text-gray-600" />
              </button>
            )}
          </div>

          <h1 className="text-xl sm:text-2xl font-bold text-header text-center flex-1">
            Request Demo
          </h1>

          <div className="w-10" />
        </div>
      </div>

      {/* Main Content Area - Restricted Width */}
      <div className="flex-1 min-h-0 flex justify-center bg-white">
        {/* update max widht to max-w-[510px] from max-w-[480px] */}
        <div className="w-full flex-1 min-h-0">
          <div className="relative bg-white rounded-lg shadow-lg border-gray-100 h-full flex flex-col overflow-hidden">
            {/* Loading and Status States */}
            {hasError ? (
              <div className="h-full flex flex-col items-center justify-center p-8 text-center">
                <div className="w-16 h-16 mb-4 bg-red-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-red-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Something went wrong
                </h3>
                <p className="text-gray-600">
                  Failed due to technical reasons. Please try again later.
                </p>
                <button
                  onClick={() => {
                    setHasError(false);
                    setIsCreatingMeeting(false);
                  }}
                  className="inline-flex mt-4 items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Try Again
                </button>
              </div>
            ) : isFetching ? (
              <div className="h-full flex flex-col items-center justify-center p-8">
                <div className="relative">
                  <div className="w-12 h-12 border-4 border-blue-200 rounded-full animate-spin border-t-blue-600"></div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">
                  Loading
                </h3>
                <p className="text-gray-600">
                  Please wait while we prepare everything...
                </p>
              </div>
            ) : isCreatingMeeting ? (
              <div className="h-full flex flex-col items-center justify-center p-8">
                <div className="relative">
                  <div className="w-12 h-12 border-4 border-green-200 rounded-full animate-spin border-t-green-600"></div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">
                  Scheduling Meeting
                </h3>
                <p className="text-gray-600">
                  We're setting up your demo session...
                </p>
              </div>
            ) : isRescheduling ? (
              <div className="h-full flex flex-col items-center justify-center p-8">
                <div className="relative">
                  <div className="w-12 h-12 border-4 border-green-200 rounded-full animate-spin border-t-green-600"></div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">
                  Re-Scheduling Meeting
                </h3>
                <p className="text-gray-600">
                  We're re-setting up your demo session...
                </p>
              </div>
            ) : isInviting ? (
              <div className="h-full flex flex-col items-center justify-center p-8">
                <div className="relative">
                  <div className="w-12 h-12 border-4 border-green-200 rounded-full animate-spin border-t-green-600"></div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">
                  Updating Meeting
                </h3>
                <p className="text-gray-600">
                  We're adding up more attendees...
                </p>
              </div>
            ) : isCancelling ? (
              <div className="h-full flex flex-col items-center justify-center p-8">
                <div className="relative">
                  <div className="w-12 h-12 border-4 border-green-200 rounded-full animate-spin border-t-green-600"></div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">
                  Updating Meeting
                </h3>
                <p className="text-gray-600">
                  We're cancelling the meeting as requested...
                </p>
              </div>
            ) : isCancelled ? (
              <div className="h-full flex flex-col items-center justify-center p-8">
                <div className="w-12 h-12 mb-4 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                  <svg
                    className="w-6 h-6 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mt-2 mb-1">
                  You Cancelled the Meeting
                </h3>
                <p className="text-gray-600 text-center max-w-sm">
                  Your meeting has been cancelled successfully. You can schedule
                  a new meeting anytime if needed.
                </p>

                <button
                  onClick={() => {
                    setIsCancelled(false);
                    setIsMeetingCreated(false);
                    setIsMeetingRescheduled(false);
                    fetchAvailableDates();
                  }}
                  className="inline-flex mt-4 items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  <Calendar1 size={16} style={{ marginRight: 5 }} />
                  Schedule a New Meetting
                </button>
              </div>
            ) : isMeetingCreated || isMeetingRescheduled ? (
              <div className="h-full w-full">
                <MeetingConfirmed
                  selectedTime={selectedTime}
                  rescheduled={isMeetingRescheduled}
                  data={finalSubmittedData}
                  eventData={eventData}
                  onReschedule={() => {
                    setDemoData(finalSubmittedData.demoData);
                    setIsMeetingCreated(false);
                    setIsRescheduleInitiated(true);
                    setCurrentStep("calendar");
                    fetchAvailableDates();
                  }}
                  onInviteMore={(emails) => {
                    addMoreAttendees(eventData.id, emails);
                  }}
                  onCancel={() => {
                    cancelEvent(eventData.id);
                  }}
                />
              </div>
            ) : null}

            {/* Calendar Step */}
            {isCreatingMeeting ||
            isMeetingCreated ||
            isRescheduling ||
            isMeetingRescheduled ||
            isCancelling ||
            isCancelled ? null : (
              <div
                className={`absolute inset-0 scrollbar-hide transition-transform duration-300 ease-in-out overflow-hidden popup-scroll ${
                  currentStep === "calendar"
                    ? "translate-x-0"
                    : "-translate-x-full"
                }`}
              >
                {resourceData && (
                  <Calendar
                    preselectedTimeZone={selectedTimeZone}
                    {...resourceData}
                    onShowDemoForm={(time) => {
                      setSelectedTime(time);
                      if (isRescheduleInitiated) {
                        initiateRescheduleRequest(eventData.id, time);
                      } else {
                        handleShowDemoForm();
                      }
                    }}
                    onSelectedTimezone={(timeZone) => {
                      setSelectedTimeZone(timeZone);
                    }}
                    onClose={() => {}}
                  />
                )}
              </div>
            )}

            {/* Demo Form Step */}
            {isCreatingMeeting ||
            isMeetingCreated ||
            isRescheduling ||
            isMeetingRescheduled ||
            isCancelling ||
            isCancelled ? null : (
              <div
                className={`absolute inset-0 scrollbar-hide transition-transform duration-300 ease-in-out overflow-hidden popup-scroll ${
                  currentStep === "demo" ? "translate-x-0" : "translate-x-full"
                }`}
              >
                <RequestDemo
                  onClose={handleCloseDemoForm}
                  data={demoData}
                  selectedTime={selectedTime}
                  requestDemo={(data) => {
                    setDemoData(data);
                    createDemoRequest(data);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
